import {
  ProviderSlotRegistry,
  buildAuthorizationRequest,
  consumeAuthorizationCallback,
  parseProviderTokens,
  validateProviderIdToken,
  validateProviderPkceAttempt,
  validateProviderSession,
  type ProviderAuthConfig,
  type ProviderAuthorizationAttempt,
  type ProviderAuthorizationPrompt,
  type ProviderSessionProjection,
  type ProviderSupportSessionProjection,
  type ProviderSlotSummary,
  type ProviderTokens,
  type StorageLike,
} from "./providerAuth.ts";

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
export interface SupportSurfaceBootstrap {
  enabled: () => boolean;
  load: (accessToken: string, subject: string) => Promise<ProviderSupportSessionProjection>;
}

export interface ProviderRefreshCoordinator {
  remember(accountUuid: string, tokens: ProviderTokens): void;
  forget(accountUuid: string): void;
  coordinate(
    accountUuid: string,
    expectedRefreshToken: string,
    exchange: () => Promise<ProviderTokens>,
  ): Promise<ProviderTokens>;
}

class LocalProviderRefreshCoordinator implements ProviderRefreshCoordinator {
  remember(): void {}
  forget(): void {}

  coordinate(
    _accountUuid: string,
    _expectedRefreshToken: string,
    exchange: () => Promise<ProviderTokens>,
  ): Promise<ProviderTokens> {
    return exchange();
  }
}

interface StoredAttempts {
  version: 1;
  attempts: Record<string, ProviderAuthorizationAttempt>;
}

const providerAttemptsKey = "wenlix-provider-attempts-v1";
const maximumPendingAttempts = 4;
const attemptLifetimeMs = 15 * 60 * 1_000;

export class ProviderRequestError extends Error {
  readonly status: number;

  constructor(operation: string, status: number) {
    super(`${operation} was rejected with status ${status}`);
    this.name = "ProviderRequestError";
    this.status = status;
  }
}

export class ProviderSessionRejectedError extends Error {
  readonly accountUuid: string;

  constructor(message: string, accountUuid: string) {
    super(message);
    this.name = "ProviderSessionRejectedError";
    this.accountUuid = accountUuid;
  }
}

async function jsonBody(response: Response, operation: string): Promise<unknown> {
  if (!response.ok) {
    throw new ProviderRequestError(operation, response.status);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error(`${operation} returned a non-JSON response`);
  }
  return body;
}

function formRequest(body: URLSearchParams): RequestInit {
  return {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  };
}

export class ProviderAuthClient {
  private readonly config: ProviderAuthConfig;
  private readonly storage: StorageLike;
  private readonly fetcher: Fetcher;
  private readonly registry: ProviderSlotRegistry;
  private readonly refreshCoordinator: ProviderRefreshCoordinator;
  private activeRefresh: {
    accountUuid: string;
    promise: Promise<ProviderTokens>;
  } | null = null;
  private sessionGeneration = 0;
  private supportBootstrap?: SupportSurfaceBootstrap;

  constructor(
    config: ProviderAuthConfig,
    storage: StorageLike,
    fetcher: Fetcher = globalThis.fetch.bind(globalThis),
    refreshCoordinator: ProviderRefreshCoordinator = new LocalProviderRefreshCoordinator(),
    supportBootstrap?: SupportSurfaceBootstrap,
  ) {
    this.config = config;
    this.storage = storage;
    this.fetcher = fetcher;
    this.registry = new ProviderSlotRegistry(storage);
    this.refreshCoordinator = refreshCoordinator;
    this.supportBootstrap = supportBootstrap;
    const active = this.registry.active();
    if (active) {
      this.refreshCoordinator.remember(active.projection.accountUuid, active.tokens);
    }
  }

  private tokenEndpoint(): string {
    return new URL("/oauth/v2/token", `${this.config.issuer}/`).toString();
  }

  private revocationEndpoint(): string {
    return new URL("/oauth/v2/revoke", `${this.config.issuer}/`).toString();
  }

  private async revokeRefreshToken(refreshToken: string): Promise<void> {
    const response = await this.fetcher(
      this.revocationEndpoint(),
      formRequest(new URLSearchParams({
        token: refreshToken,
        token_type_hint: "refresh_token",
        client_id: this.config.clientId,
      })),
    );
    if (!response.ok) {
      throw new ProviderRequestError("provider revocation", response.status);
    }
  }

  private jwksEndpoint(): string {
    return new URL("/oauth/v2/keys", `${this.config.issuer}/`).toString();
  }

  private readAttempts(now: number = Date.now()): Map<string, ProviderAuthorizationAttempt> {
    const stored = this.storage.getItem(providerAttemptsKey);
    if (!stored) return new Map();
    try {
      const parsed = JSON.parse(stored) as StoredAttempts;
      if (parsed.version !== 1 || !parsed.attempts || typeof parsed.attempts !== "object") {
        throw new Error("unsupported attempts");
      }
      const attempts = new Map<string, ProviderAuthorizationAttempt>();
      for (const [state, attempt] of Object.entries(parsed.attempts)) {
        const prompt: ProviderAuthorizationPrompt = attempt.prompt === undefined
          ? "login"
          : attempt.prompt;
        if (
          attempt.state !== state ||
          typeof attempt.nonce !== "string" ||
          typeof attempt.verifier !== "string" ||
          typeof attempt.challenge !== "string" ||
          attempt.redirectUri !== this.config.redirectUri ||
          !(attempt.expectedAccountUuid === null ||
            (typeof attempt.expectedAccountUuid === "string" &&
              /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(attempt.expectedAccountUuid))) ||
          (prompt !== "login" && prompt !== "none") ||
          typeof attempt.createdAt !== "number" ||
          !/^[A-Za-z0-9_-]{32,}$/.test(attempt.state) ||
          !/^[A-Za-z0-9_-]{32,}$/.test(attempt.nonce) ||
          !/^[A-Za-z0-9._~-]{43,128}$/.test(attempt.verifier) ||
          !/^[A-Za-z0-9_-]{43}$/.test(attempt.challenge)
        ) {
          throw new Error("invalid attempt");
        }
        if (attempt.createdAt <= now && now - attempt.createdAt <= attemptLifetimeMs) {
          attempts.set(state, { ...attempt, prompt });
        }
      }
      return attempts;
    } catch {
      this.storage.removeItem(providerAttemptsKey);
      return new Map();
    }
  }

  private writeAttempts(attempts: Map<string, ProviderAuthorizationAttempt>): void {
    if (attempts.size === 0) {
      this.storage.removeItem(providerAttemptsKey);
      return;
    }
    this.storage.setItem(providerAttemptsKey, JSON.stringify({
      version: 1,
      attempts: Object.fromEntries(attempts),
    } satisfies StoredAttempts));
  }

  async begin(
    now: number = Date.now(),
    loginHint?: string,
    prompt: ProviderAuthorizationPrompt = "login",
  ): Promise<string> {
    const request = await buildAuthorizationRequest(
      this.config,
      globalThis.crypto,
      now,
      loginHint,
      prompt,
    );
    const attempts = this.readAttempts(now);
    attempts.set(request.state, {
      state: request.state,
      nonce: request.nonce,
      verifier: request.verifier,
      challenge: request.challenge,
      redirectUri: request.redirectUri,
      expectedAccountUuid: request.expectedAccountUuid,
      prompt: request.prompt,
      createdAt: request.createdAt,
    });
    while (attempts.size > maximumPendingAttempts) {
      const oldest = [...attempts.values()].sort((left, right) => left.createdAt - right.createdAt)[0];
      attempts.delete(oldest.state);
    }
    this.writeAttempts(attempts);
    return request.authorizationUrl;
  }

  async beginSessionRestore(
    expectedAccountUuid: string,
    now: number = Date.now(),
  ): Promise<string> {
    const loginHint = `w_m_${expectedAccountUuid.replaceAll("-", "")}`;
    return this.begin(now, loginHint, "none");
  }

  async complete(callbackUrl: string, now: number = Date.now()): Promise<ProviderSessionProjection> {
    const attempts = this.readAttempts(now);
    const callback = (() => {
      try {
        return consumeAuthorizationCallback(callbackUrl, attempts, now);
      } finally {
        this.writeAttempts(attempts);
      }
    })();
    const { code, attempt } = callback;
    await validateProviderPkceAttempt(attempt);

    const tokenResponse = await this.fetcher(
      this.tokenEndpoint(),
      formRequest(new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: attempt.redirectUri,
        client_id: this.config.clientId,
        code_verifier: attempt.verifier,
      })),
    );
    const rawTokenResponse = await jsonBody(tokenResponse, "provider token exchange");
    const tokens = parseProviderTokens(rawTokenResponse, now);
    const jwksResponse = await this.fetcher(this.jwksEndpoint(), {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
    });
    const rawJwks = await jsonBody(jwksResponse, "provider JWKS");
    const idTokenSubject = await validateProviderIdToken(
      rawTokenResponse,
      tokens.accessToken,
      attempt.nonce,
      this.config,
      rawJwks,
      globalThis.crypto,
      now,
    );

    let sessionResponse: Response | undefined;
    try { sessionResponse = await this.fetcher(this.config.sessionEndpoint, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      credentials: "same-origin",
      cache: "no-store",
    }); } catch (error) {
      if (!(error instanceof TypeError) || !this.supportBootstrap?.enabled()) throw error;
    }
    let projection: ProviderSessionProjection;
    if ((!sessionResponse || sessionResponse.status >= 500) && this.supportBootstrap?.enabled()) {
      // The ordinary verifier remains unchanged. The narrowly scoped fallback
      // additionally proves the signed internal organization before SupportMe.
      await validateProviderIdToken(rawTokenResponse, tokens.accessToken, attempt.nonce, this.config,
        rawJwks, globalThis.crypto, now, this.config.internalOrganizationId);
      projection = await this.supportBootstrap.load(tokens.accessToken, idTokenSubject);
    } else {
      if (!sessionResponse) throw new Error("provider session is unavailable");
      projection = validateProviderSession(
      await jsonBody(sessionResponse, "provider session bootstrap"),
      this.config,
      );
    }
    if (projection.subject !== idTokenSubject) {
      throw new ProviderSessionRejectedError("provider session subject mismatch", projection.accountUuid);
    }
    if (attempt.expectedAccountUuid !== null && projection.accountUuid !== attempt.expectedAccountUuid) {
      throw new ProviderSessionRejectedError("provider selected account mismatch", projection.accountUuid);
    }
    this.registry.put(projection, tokens);
    this.refreshCoordinator.remember(projection.accountUuid, tokens);
    return projection;
  }

  activeTokens(): ProviderTokens | null {
    return this.registry.active()?.tokens ?? null;
  }

  activeProjection(): ProviderSessionProjection | null {
    return this.registry.active()?.projection ?? null;
  }

  listSlots(): ProviderSlotSummary[] {
    return this.registry.list();
  }

  async activate(accountUuid: string): Promise<ProviderSessionProjection> {
    const previousAccountUuid = this.registry.active()?.projection.accountUuid ?? null;
    this.registry.activate(accountUuid);
    try {
      return await this.reloadActiveSession();
    } catch (error) {
      const activeAccountUuid = this.registry.active()?.projection.accountUuid ?? null;
      if (activeAccountUuid === null || activeAccountUuid === accountUuid) {
        if (previousAccountUuid && this.registry.projection(previousAccountUuid)) {
          this.registry.activate(previousAccountUuid);
        } else {
          this.registry.deactivate();
        }
      }
      throw error;
    }
  }

  deactivate(): void {
    this.registry.deactivate();
  }

  discardActive(): void {
    const accountUuid = this.registry.active()?.projection.accountUuid;
    if (accountUuid) {
      this.sessionGeneration += 1;
      this.registry.remove(accountUuid);
      this.refreshCoordinator.forget(accountUuid);
    }
  }

  discard(accountUuid: string): void {
    if (this.registry.active()?.projection.accountUuid === accountUuid) {
      this.sessionGeneration += 1;
    }
    this.registry.remove(accountUuid);
    this.refreshCoordinator.forget(accountUuid);
  }

  activeAccountUuid(): string | null {
    return this.registry.active()?.projection.accountUuid ?? null;
  }

  async refreshActive(now: number = Date.now()): Promise<ProviderTokens> {
    const current = this.registry.active();
    if (!current) throw new Error("provider slot is not active");
    const accountUuid = current.projection.accountUuid;
    if (this.activeRefresh) {
      if (this.activeRefresh.accountUuid !== accountUuid) {
        throw new Error("provider refresh already belongs to another account slot");
      }
      return this.activeRefresh.promise;
    }

    const expectedRefreshToken = current.tokens.refreshToken;
    const expectedSessionGeneration = this.sessionGeneration;
    const promise = (async () => {
      try {
        const tokens = await this.refreshCoordinator.coordinate(
          accountUuid,
          expectedRefreshToken,
          async () => {
            const response = await this.fetcher(
              this.tokenEndpoint(),
              formRequest(new URLSearchParams({
                grant_type: "refresh_token",
                refresh_token: expectedRefreshToken,
                client_id: this.config.clientId,
              })),
            );
            return parseProviderTokens(await jsonBody(response, "provider refresh"), now);
          },
        );
        if (this.sessionGeneration !== expectedSessionGeneration) {
          try {
            await this.revokeRefreshToken(tokens.refreshToken);
          } catch {
            // Local logout remains fail-closed even when remote revocation is unavailable.
          }
          throw new ProviderSessionRejectedError(
            "provider session changed during refresh",
            accountUuid,
          );
        }
        if (tokens.refreshToken === expectedRefreshToken) {
          throw new ProviderSessionRejectedError(
            "provider refresh token was not rotated",
            accountUuid,
          );
        }
        this.registry.rotateActiveTokens(accountUuid, expectedRefreshToken, tokens);
        this.refreshCoordinator.remember(accountUuid, tokens);
        return tokens;
      } catch (error) {
        if (
          error instanceof ProviderSessionRejectedError ||
          (error instanceof ProviderRequestError && (error.status === 400 || error.status === 401))
        ) {
          if (this.registry.removeIfRefreshToken(accountUuid, expectedRefreshToken)) {
            this.refreshCoordinator.forget(accountUuid);
          }
          throw new ProviderSessionRejectedError(error.message, accountUuid);
        }
        throw error;
      }
    })();
    this.activeRefresh = { accountUuid, promise };
    try {
      return await promise;
    } finally {
      if (this.activeRefresh?.promise === promise) this.activeRefresh = null;
    }
  }

  async reloadActiveSession(): Promise<ProviderSessionProjection> {
    const current = this.registry.active();
    if (!current) throw new Error("provider slot is not active");

    const load = (accessToken: string) => this.fetcher(this.config.sessionEndpoint, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: "same-origin",
      cache: "no-store",
    });

    let response: Response;
    let sawUnauthorized = false;
    try {
      response = await load(current.tokens.accessToken);
      if (response.status === 401) {
        sawUnauthorized = true;
        const refreshed = await this.refreshActive();
        response = await load(refreshed.accessToken);
      }
    } catch (error) {
      if (error instanceof ProviderSessionRejectedError) throw error;
      if (error instanceof TypeError && !sawUnauthorized && this.supportBootstrap?.enabled()) {
        const projection = await this.supportBootstrap.load(current.tokens.accessToken, current.projection.subject);
        if (this.registry.active()?.projection.accountUuid !== current.projection.accountUuid) throw new ProviderSessionRejectedError("provider session changed", current.projection.accountUuid);
        this.registry.updateActiveSurfaceProjection(projection);
        return projection;
      }
      const message = error instanceof ProviderRequestError
        ? error.message
        : "provider session is unavailable";
      throw new ProviderSessionRejectedError(message, current.projection.accountUuid);
    }
    try {
      if (response.status >= 500 && !sawUnauthorized && this.supportBootstrap?.enabled()) {
        const active = this.registry.active();
        if (!active || active.projection.accountUuid !== current.projection.accountUuid) throw new Error("provider session changed");
        const projection = await this.supportBootstrap.load(active.tokens.accessToken, current.projection.subject);
        if (this.registry.active()?.projection.accountUuid !== current.projection.accountUuid) throw new Error("provider session changed");
        this.registry.updateActiveSurfaceProjection(projection);
        return projection;
      }
      const projection = validateProviderSession(
        await jsonBody(response, "provider session bootstrap"),
        this.config,
      );
      if (current.projection.supportOnly) this.registry.updateActiveSurfaceProjection(projection);
      else this.registry.updateActiveProjection(projection);
      return projection;
    } catch (error) {
      if (error instanceof ProviderRequestError && ![401, 403].includes(error.status)) {
        throw new ProviderSessionRejectedError(error.message, current.projection.accountUuid);
      }
      this.registry.remove(current.projection.accountUuid);
      const message = error instanceof Error ? error.message : "provider session was rejected";
      throw new ProviderSessionRejectedError(message, current.projection.accountUuid);
    }
  }

  async logoutActive(): Promise<void> {
    const current = this.registry.active();
    if (!current) return;
    this.sessionGeneration += 1;
    this.registry.remove(current.projection.accountUuid);
    this.refreshCoordinator.forget(current.projection.accountUuid);
    await this.revokeRefreshToken(current.tokens.refreshToken);
  }
}
